// Backend shartnomasi (`aimly-back/src/modules/admin/dto/admin.dto.ts`).
// Qo'lda sinxronlanadi: maydon o'zgarsa — shu yerda ham, bitta MR da.

export type Language = "uz" | "ru" | "en";
export type DeliveryChannel = "WEB" | "TELEGRAM";
export type DeliveryStatus = "PENDING" | "SENT" | "FAILED" | "READ" | "SKIPPED";
export type NotificationType =
  | "DAILY_PLAN"
  | "PLANNING_REMINDER"
  | "TASK_REMINDER"
  | "TASK_OVERDUE"
  | "TASK_POSTPONED"
  | "DAILY_REVIEW"
  | "WEEKLY_REVIEW"
  | "SYSTEM";

export const DELIVERY_STATUSES: DeliveryStatus[] = ["PENDING", "SENT", "FAILED", "READ", "SKIPPED"];
export const DELIVERY_CHANNELS: DeliveryChannel[] = ["WEB", "TELEGRAM"];

export interface ApiEnvelope<T> {
  success: true;
  message: string | null;
  data: T;
  timestamp: string;
}

export interface PageMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PageMeta;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> };
  requestId: string;
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export interface SessionUser {
  id: string;
  telegramUserId: string;
  telegramUsername: string | null;
  firstName: string | null;
  lastName: string | null;
  language: Language;
}

export interface AuthSession {
  accessToken: string;
  expiresIn: number;
  user: SessionUser;
}

export interface AuthConfig {
  botUsername: string | null;
  telegramEnabled: boolean;
  devLoginEnabled: boolean;
}

export interface BotLoginRequest {
  token: string;
  botUrl: string;
  expiresAt: string;
}

export interface BotLoginPollResult {
  status: "PENDING" | "CONFIRMED";
  session: AuthSession | null;
}

export interface AdminMe {
  id: string;
  firstName: string | null;
  telegramUsername: string | null;
  isAdmin: boolean;
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export const ACTIVE_WINDOWS = [1, 7, 30] as const;
export type ActiveWindow = (typeof ACTIVE_WINDOWS)[number];
export const LANGUAGES: Language[] = ["uz", "ru", "en"];

export interface DeliveryStat {
  channel: DeliveryChannel;
  status: DeliveryStatus;
  count: number;
}

export interface SeriesPoint {
  date: string;
  signups: number;
  activeUsers: number;
  tasksCreated: number;
  tasksCompleted: number;
}

export interface TopUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  telegramUsername: string | null;
  telegramUserId: string;
  completed: number;
  planned: number;
}

export interface AdminDashboard {
  from: string;
  to: string;
  bucket: "day" | "week" | "month";
  activeDays: ActiveWindow;
  users: {
    total: number;
    new: number;
    active: number;
    inactive: number;
    neverActivated: number;
    atRisk: number;
    blocked: number;
    reachable: number;
    dau: number;
    wau: number;
    mau: number;
  };
  languages: { language: Language; count: number }[];
  tasks: {
    created: number;
    completed: number;
    postponed: number;
    cancelled: number;
    planned: number;
    completionRate: number;
    focusMinutes: number;
  };
  goals: { created: number; completed: number; active: number };
  reviews: { daily: number; weekly: number; period: number };
  previous: {
    newUsers: number;
    activeUsers: number;
    tasksCreated: number;
    tasksCompleted: number;
    completionRate: number;
  };
  series: SeriesPoint[];
  completionsByHour: number[];
  completionsByWeekday: number[];
  funnel: { registered: number; createdTask: number; completedTask: number; wroteReview: number };
  topUsers: TopUser[];
  deliveries: DeliveryStat[];
}

// ─── Foydalanuvchi statistikasi va jurnali ───────────────────────────────────

export const STATS_PERIODS = ["day", "week", "month", "year"] as const;
export type StatsPeriod = (typeof STATS_PERIODS)[number];

export interface UserStatsTotals {
  planned: number;
  completed: number;
  postponed: number;
  cancelled: number;
  created: number;
  completionRate: number;
  focusMinutes: number;
  goalsCreated: number;
  goalsCompleted: number;
  reviews: number;
  activeDays: number;
  logins: number;
}

export interface UserStats {
  period: StatsPeriod;
  from: string;
  to: string;
  totals: UserStatsTotals;
  previous: UserStatsTotals;
  buckets: { key: string; planned: number; completed: number; created: number }[];
  byPriority: { priority: "HIGH" | "MEDIUM" | "LOW"; planned: number; completed: number }[];
  calendar: { date: string; count: number }[];
  lastActiveAt: string | null;
}

export const ACTIVITY_GROUPS = ["session", "task", "goal", "review", "notification"] as const;
export type ActivityGroup = (typeof ACTIVITY_GROUPS)[number];
export type ActivityType =
  | "LOGIN"
  | "TASK_CREATED"
  | "TASK_COMPLETED"
  | "TASK_POSTPONED"
  | "TASK_CANCELLED"
  | "GOAL_CREATED"
  | "GOAL_COMPLETED"
  | "GOAL_ARCHIVED"
  | "DAILY_REVIEW"
  | "WEEKLY_REVIEW"
  | "PERIOD_REVIEW"
  | "NOTIFICATION_READ";

export interface ActivityItem {
  id: string;
  type: ActivityType;
  at: string;
  title: string | null;
  detail: string | null;
}

// ─── Foydalanuvchilar ────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  telegramUserId: string;
  telegramUsername: string | null;
  firstName: string | null;
  lastName: string | null;
  language: Language;
  timezone: string;
  telegramCanMessage: boolean;
  isBlocked: boolean;
  isAdmin: boolean;
  taskCount: number;
  completedTaskCount: number;
  goalCount: number;
  lastLoginAt: string | null;
  createdAt: string;
  lastActiveAt: string | null;
}

export interface AdminUserDetail extends AdminUser {
  morningTime: string;
  planningTime: string;
  reviewTime: string;
  dailyReviewCount: number;
  weeklyReviewCount: number;
  completed7d: number;
  failedDeliveries: number;
}

// ─── Yetkazishlar ────────────────────────────────────────────────────────────

export interface AdminDelivery {
  id: string;
  channel: DeliveryChannel;
  status: DeliveryStatus;
  attempts: number;
  error: string | null;
  sentAt: string | null;
  createdAt: string;
  notificationType: NotificationType;
  title: string;
  userId: string;
  userName: string | null;
  telegramUserId: string;
}

export interface RetryResult {
  id: string;
  status: DeliveryStatus;
}

// ─── E'lonlar ────────────────────────────────────────────────────────────────

export interface Announcement {
  id: string;
  title: string;
  body: string;
  targetUserId: string | null;
  recipientCount: number;
  createdAt: string;
}

export interface AnnouncementInput {
  title: string;
  body: string;
  telegramUserId?: string;
}
